import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { AiInputAction } from 'juneau';
import { Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { QuickActionsModal } from './QuickActionsModal';
import { ChatHelpModal } from './ChatHelpModal';

interface ChatInputActions {
  /** Toolbar actions rendered next to the send button, replacing juneau's built-ins. */
  actions: AiInputAction[];
  /** Must be rendered inside AiChatProvider — the quick-action picker sends messages. */
  dialogs: ReactNode;
}

/** Wires the chat toolbar buttons to their modals. */
export function useChatInputActions(): ChatInputActions {
  const t = useFeatureTranslation('aiChat');
  const [openModal, setOpenModal] = useState<'quickActions' | 'help' | null>(null);

  const close = useCallback((): void => setOpenModal(null), []);

  // Memoised so the composer toolbar keeps one identity — the layout above
  // re-renders on every navigation and panel state change.
  const actions: AiInputAction[] = useMemo(
    () => [
      {
        icon: <Icon name='bolt' className='h-4 w-4' />,
        label: t('inputActions.quickActions'),
        onClick: () => setOpenModal('quickActions')
      },
      {
        icon: <Icon name='life-ring' className='h-4 w-4' />,
        label: t('inputActions.help'),
        onClick: () => setOpenModal('help')
      }
    ],
    [t]
  );

  const dialogs = useMemo(
    () => (
      <>
        <QuickActionsModal open={openModal === 'quickActions'} onClose={close} />
        <ChatHelpModal open={openModal === 'help'} onClose={close} />
      </>
    ),
    [openModal, close]
  );

  return { actions, dialogs };
}
